import type { DocumentMetadataItem } from '@lds-efsp/shared-contracts';
import { countySlug } from './county-slug';

/**
 * Court-required Refers-To corrections, per county (Walter R100, LegalConnect
 * parity, WOL-446). The JTI policy's DOCUMENT metadata is the normal source of
 * required per-doc fields (requiredness = presence in the list), but the
 * production reference (LegalConnect) shows Madera requires "Refers To (as to
 * parties)" on an initiating Complaint while the JTI policy carries no
 * refers-to code on that doc type at all. This map patches the served policy
 * client-side: doc types whose NAME matches the county's pattern get the
 * synthetic REFERS_TO_PARTIES item appended, which makes it required
 * (validateDocumentMetadata), rendered (DocumentAssignmentSection keeps every
 * caseParticipant item under includeAllPartyFields), and emitted (stage-3's
 * refersToSources already includes REFERS_TO_PARTIES).
 *
 * Name-matched, not code-matched: the Complaint family spans several policy
 * codes (Complaint, Complaint: Personal Injury, Complaint:SC Juris Limit, …)
 * and Walter's requirement is "a Complaint (and other doc types the court
 * flags)". Applied only under ?confirmFlow=1 — the classic flow's Upload form
 * keeps the two-code filter and could render but never satisfy the field, so
 * the frozen surface must not see it.
 */
const REFERS_TO_REQUIRED_BY_COUNTY: Record<string, RegExp> = {
  madera: /^complaint\b/i,
};

/** Any of these codes already carries the doc's refers-to role — never double up. */
const REFERS_TO_CODES = ['RESPONDING_PARTY', 'PARTY_SERVED', 'REPRESENTING', 'REFERS_TO_PARTIES'];

/** Same shape DocumentsTab synthesizes for the auto-derived display field. */
export const REFERS_TO_PARTIES_ITEM: DocumentMetadataItem = {
  code: 'REFERS_TO_PARTIES',
  name: 'Refers To (as to parties)',
  multiple: true,
  classType: 'caseParticipant',
  subType: 'refers-to',
  valueRestriction: 'existing-data',
  additionalInfoTags: [],
};

/**
 * Patch a fetched document-types list with the county's Refers-To requirement.
 * Pure; returns the input array untouched when the flag is off, the county has
 * no correction, or a doc type already carries a refers-to code.
 */
export function applyRefersToRequirement<
  T extends { name: string; metadata?: DocumentMetadataItem[] },
>(
  docTypes: T[],
  county: string | null | undefined,
  confirmFlowEnabled: boolean,
): T[] {
  if (!confirmFlowEnabled || !county) return docTypes;
  const matcher = REFERS_TO_REQUIRED_BY_COUNTY[countySlug(county)];
  if (!matcher) return docTypes;

  return docTypes.map(dt => {
    if (!matcher.test(dt.name)) return dt;
    const metadata = dt.metadata ?? [];
    if (metadata.some(m => REFERS_TO_CODES.includes(m.code))) return dt;
    return { ...dt, metadata: [...metadata, REFERS_TO_PARTIES_ITEM] };
  });
}
